/**
 * Softwise M10b — Line step in memory order (how players recall a hand).
 *
 *   preflop actions → flop 3 cards → flop actions → turn 1 card → … → river.
 *
 * - Tap-once: fold / check / call / all-in record immediately; raise / bet
 *   open inline bb size chips (`2.5` `3` `직접`). No Add button.
 * - Legal actions only (derived by lib/handFlow from what was recorded).
 * - Board cards are asked inline when a street closes with 2+ players left;
 *   skipped when one player remains. No separate board step.
 * - Stacked line on top = lib/lineText (same text as Share / Copy / PNG);
 *   tap a segment to re-enter from there.
 * - One filled button max: only the custom-size 「확인」.
 * Locks: process sharing only (no metrics, advice or bare percent signs), the
 * app never inserts actions, not-involved seats are never shown (#61).
 * Softwise M10f: 「기록 안 함」 empty until pressed (Soft-safe ⑤).
 * Soft Quiet C2: 「기억 안 남」 mid-cut empty until pressed (Soft-safe ⑤).
 * Soft G: optional 「결말 (선택)」 row after the line — closed <details>, empty
 * until pressed, seat-subject chips of equal weight (no auto-fill, ever).
 */
import { t } from '../../i18n/strings'
import { endingSeats } from '../../lib/endingRecord'
import {
  deriveHandFlow,
  formatBb,
  type SeatOptions,
} from '../../lib/handFlow'
import {
  ACTION_LABEL,
  STREET_LABEL,
  SUIT_SYMBOL,
  formatSeatCode,
  handLineSegments,
  handSettingsText,
  usedCards,
} from '../../lib/poker'
import type { ActionType, Card, HandState } from '../../lib/types'
import {
  addEndingShownCard,
  assignFlowBoardCard,
  clearEndingShown,
  clearFlowBoardSlot,
  cutFlowMemory,
  getSessionHandIndex,
  getState,
  isHeroSeatPicked,
  isReceivedFromLink,
  openSettings,
  markFlowUnrecorded,
  recordFlowAction,
  requestRender,
  selectFlowSeat,
  selectedFlowSeat,
  setEndingPot,
  setEndingShownMark,
} from '../../state'
import { cardGrid, playingCard } from '../cards'
import { el } from '../dom'
import { requestStep } from './helpers'

/** Inline size chips open for this aggressive type (null = closed). */
let sizeOpenFor: 'bet' | 'raise' | null = null
/** Custom bb input open (inside the size chips). */
let customSizeOpen = false
let customSizeDraft = ''
/** Soft G: 「결말 (선택)」 details open (closed by default; user toggles). */
let endingOpen = false
/** Soft G: seat whose showdown cards are being entered (null = none). */
let endingPickSeat: string | null = null
/**
 * Soft G A-reset: which hand the ending open state belongs to. The new-hand /
 * reset path (`resetHand`) bumps the session hand index and a `#/h/` hydrate
 * flips the received flag — either one means a different hand, so the row goes
 * back to closed + no card entry. Staying open within the same hand is fine.
 */
let endingHandKey = ''

function syncEndingHand(): void {
  const key = `${getSessionHandIndex()}:${isReceivedFromLink() ? 'r' : 'o'}`
  if (key === endingHandKey) return
  endingHandKey = key
  endingOpen = false
  endingPickSeat = null
}

/** Soft-safe Move 3 compat: clear transient Line UI when leaving the step. */
export function resetActorCustomDetails(): void {
  sizeOpenFor = null
  customSizeOpen = false
  customSizeDraft = ''
  endingPickSeat = null
}

function rerender(): void {
  // Local UI flags only (size chips) — no hand change.
  requestRender()
}

/**
 * Stacked progress line (the single line generator) inside the closed
 * 「지금까지 줄」 peek: settings / seat / hole segments jump back to edit.
 * Soft I1a: tap-rewind moved to the always-visible sticky strip
 * (ui/inputStrip.ts) — the peek re-rendered closed on every tap, which hid
 * its 「여기부터 다시」 row. Action / board segments here are read-only.
 */
export function renderLineStrip(opts: { interactive: boolean }): HTMLElement | null {
  const ui = t()
  const { hand } = getState()
  const segs = handLineSegments(hand, { seatPicked: isHeroSeatPicked() })
  const kids: HTMLElement[] = []
  const sep = () =>
    el('span', { className: 'lineStripSep', text: '·', attrs: { 'aria-hidden': 'true' } })
  // Softwise M10c: dim defaults segment first (`6명 · 0.5/1 · 100bb`) — tap
  // opens the settings side screen. Not part of the hand line text.
  const settingsText = handSettingsText(hand)
  kids.push(
    el('button', {
      className: 'lineSeg lineSegSettings',
      attrs: { type: 'button', 'data-seg': 'settings', 'aria-label': `${ui.settingsSegAria}: ${settingsText}` },
      text: settingsText,
      on: { click: () => openSettings() },
    }),
  )
  segs.forEach((seg) => {
    kids.push(sep())
    if (seg.kind === 'hero') {
      // Softwise M10c: seat and hole cards are each tappable (jump back to edit).
      kids.push(
        el('span', { className: 'lineSeg lineSegHero', attrs: { 'data-seg': 'hero' } }, [
          el('button', {
            className: 'lineSegPart lineSegSeat',
            attrs: { type: 'button', 'data-seg-part': 'seat', 'aria-label': `${ui.lineSeatEditAria}: ${seg.seat}` },
            text: seg.seat,
            on: { click: () => requestStep('position') },
          }),
          seg.hole
            ? el('button', {
                className: 'lineSegPart lineSegHole',
                attrs: { type: 'button', 'data-seg-part': 'hole', 'aria-label': `${ui.lineHoleEditAria}: ${seg.hole}` },
                text: seg.hole,
                on: { click: () => requestStep('cards') },
              })
            : null,
        ]),
      )
      return
    }
    void opts.interactive
    kids.push(el('span', { className: 'lineSeg lineSegStatic', attrs: { 'data-seg': seg.kind }, text: seg.text }))
  })
  return el('div', { className: 'lineStripWrap' }, [
    el(
      'p',
      {
        className: 'lineStrip',
        attrs: {
          role: 'status',
          'aria-label': ui.lineStripAria,
          'data-line-text': segs.map((s) => s.text).join(' · '),
          'data-settings-text': settingsText,
        },
      },
      kids,
    ),
  ])
}

function seatLabel(seat: string, hand: HandState): string {
  const code = formatSeatCode(seat, hand.playerCount)
  return seat === hand.heroPosition ? t().heroChip(code) : code
}

function renderBoardAsk(street: 'flop' | 'turn' | 'river', hand: HandState): HTMLElement {
  const ui = t()
  const used = usedCards(hand)
  const title =
    street === 'flop' ? ui.flowAskFlop : street === 'turn' ? ui.flowAskTurn : ui.flowAskRiver
  const slots: HTMLElement[] =
    street === 'flop'
      ? ([0, 1, 2] as const).map((i) =>
          playingCard({
            card: hand.board.flop[i],
            large: true,
            emptyLabel: ui.flopSlot(i + 1),
            selected: !hand.board.flop[i] && hand.board.flop.findIndex((c) => !c) === i,
            onClick: () => {
              if (hand.board.flop[i]) clearFlowBoardSlot('flop', i)
            },
          }),
        )
      : [
          playingCard({
            card: street === 'turn' ? hand.board.turn : hand.board.river,
            large: true,
            emptyLabel: street === 'turn' ? ui.turn : ui.river,
            selected: true,
          }),
        ]
  return el('div', { className: 'flowBoardAsk', attrs: { 'data-flow-board': street } }, [
    el('div', { className: 'flowStreetLabel', text: title }),
    el('div', { className: 'slotRow' }, slots),
    cardGrid({ used, onPick: (c: Card) => assignFlowBoardCard(c) }),
  ])
}

function renderSizeChips(type: 'bet' | 'raise', opts: SeatOptions): HTMLElement {
  const ui = t()
  const chips = opts.sizeChips.map((n) =>
    el('button', {
      className: 'chip flowSizeChip',
      attrs: { type: 'button', 'data-size-bb': String(n) },
      text: formatBb(n),
      on: {
        click: () => {
          sizeOpenFor = null
          customSizeOpen = false
          recordFlowAction(type, n)
        },
      },
    }),
  )
  chips.push(
    el('button', {
      className: ['chip', 'flowSizeChip', 'flowSizeCustomChip', customSizeOpen ? 'chipActive' : '']
        .filter(Boolean)
        .join(' '),
      attrs: { type: 'button', 'aria-pressed': customSizeOpen ? 'true' : 'false' },
      text: ui.flowSizeCustom,
      on: {
        click: () => {
          customSizeOpen = !customSizeOpen
          rerender()
        },
      },
    }),
  )
  const parsed = Number(customSizeDraft.trim())
  const customValid = customSizeDraft.trim() !== '' && Number.isFinite(parsed) && parsed > opts.minSize
  let confirmBtn: HTMLButtonElement | null = null
  const customRow = customSizeOpen
    ? el('div', { className: 'flowCustomRow' }, [
        el('label', { className: 'flowCustomField' }, [
          el('input', {
            className: 'input flowCustomInput',
            attrs: {
              type: 'text',
              inputmode: 'decimal',
              autocomplete: 'off',
              value: customSizeDraft,
              placeholder: String(opts.sizeChips[0] ?? 2.5),
              'aria-label': ui.flowSizeLabel,
            },
            on: {
              input: (e) => {
                customSizeDraft = (e.target as HTMLInputElement).value
                const n = Number(customSizeDraft.trim())
                const ok = customSizeDraft.trim() !== '' && Number.isFinite(n) && n > opts.minSize
                if (confirmBtn) confirmBtn.disabled = !ok
              },
              keydown: (e) => {
                if ((e as KeyboardEvent).key === 'Enter') confirmBtn?.click()
              },
            },
          }),
          el('span', { className: 'flowCustomUnit', text: 'bb' }),
        ]),
        (confirmBtn = el('button', {
          // The only filled (primary) button on the Line screen.
          className: 'btn btnPrimary flowSizeConfirm',
          attrs: { type: 'button', disabled: customValid ? undefined : true },
          text: ui.flowSizeConfirm,
          on: {
            click: () => {
              const n = Number(customSizeDraft.trim())
              if (!(Number.isFinite(n) && n > opts.minSize)) return
              sizeOpenFor = null
              customSizeOpen = false
              customSizeDraft = ''
              recordFlowAction(type, n)
            },
          },
        }) as HTMLButtonElement),
      ])
    : null
  return el('div', { className: 'flowSizeRow', attrs: { 'data-size-for': type } }, [
    el('span', { className: 'label', text: ui.flowSizeLabel }),
    el('div', { className: 'actorChips flowSizeChips' }, chips),
    customRow,
  ])
}

function renderActionPad(hand: HandState): HTMLElement | null {
  const ui = t()
  const flow = deriveHandFlow(hand)
  if (flow.phase.kind !== 'actions') return null
  const seat = selectedFlowSeat(hand)
  if (!seat) return null
  const opts = flow.optionsFor(seat)
  if (!opts) return null
  if (sizeOpenFor && !opts.legal.includes(sizeOpenFor)) sizeOpenFor = null

  const seatChips = el(
    'div',
    { className: 'actorChips actorChipRow flowSeatChips' },
    flow.pending.map((s) =>
      el('button', {
        className: ['chip', s === seat ? 'chipActive' : ''].filter(Boolean).join(' '),
        attrs: { type: 'button', 'data-actor': s, 'aria-pressed': s === seat ? 'true' : 'false' },
        text: seatLabel(s, hand),
        on: {
          click: () => {
            sizeOpenFor = null
            customSizeOpen = false
            selectFlowSeat(s)
          },
        },
      }),
    ),
  )

  // Softwise M10f Soft-safe ⑤: 「기록 안 함」 only for blind posters still
  // pending — empty until pressed (never chipActive / pre-selected).
  const canUnrecord = flow.unrecordable.includes(seat)
  const unrecordChip = canUnrecord
    ? el('button', {
        className: 'chip flowActionChip flowUnrecordChip',
        attrs: {
          type: 'button',
          'data-flow-unrecord': seat,
          'aria-pressed': 'false',
        },
        text: ui.flowSeatUnrecorded,
        on: {
          click: () => {
            sizeOpenFor = null
            customSizeOpen = false
            markFlowUnrecorded(seat)
          },
        },
      })
    : null

  return el('div', { className: 'flowActionPad', attrs: { 'data-flow-street': flow.phase.street } }, [
    el('div', { className: 'flowStreetLabel', text: STREET_LABEL[flow.phase.street] }),
    el('div', { className: 'field' }, [el('span', { className: 'label', text: ui.flowWhoActs }), seatChips]),
    el('div', { className: 'field' }, [
      el('span', { className: 'label', text: ui.action }),
      el('div', { className: 'actorChips actionTypeChips flowActionChips' }, [
        ...opts.legal.map((type: ActionType) =>
          el('button', {
            className: ['chip', 'flowActionChip', sizeOpenFor === type ? 'chipActive' : '']
              .filter(Boolean)
              .join(' '),
            attrs: { type: 'button', 'data-action-type': type },
            text: ACTION_LABEL[type] ?? type,
            on: {
              click: () => {
                if (type === 'bet' || type === 'raise') {
                  sizeOpenFor = sizeOpenFor === type ? null : type
                  customSizeOpen = false
                  customSizeDraft = ''
                  rerender()
                  return
                }
                sizeOpenFor = null
                customSizeOpen = false
                recordFlowAction(type)
              },
            },
          }),
        ),
        unrecordChip,
      ]),
    ]),
    sizeOpenFor ? renderSizeChips(sizeOpenFor, opts) : null,
  ])
}

/**
 * Quiet status row for skipped seats: blind posters read 「기록 안 함」
 * (their blind stays in the pot), seats that posted nothing read
 * 「참여 안 함」. Never part of the line and never a fold.
 */
function renderSkippedSeats(hand: HandState, status: Record<string, 'unrecorded' | 'notInvolved'>): HTMLElement | null {
  const ui = t()
  const groups: Array<['unrecorded' | 'notInvolved', string]> = [
    ['unrecorded', ui.flowSeatUnrecorded],
    ['notInvolved', ui.flowSeatNotInvolved],
  ]
  const kids = groups
    .map(([kind, label]) => {
      const seats = Object.keys(status).filter((s) => status[s] === kind)
      if (!seats.length) return null
      return el('span', {
        className: 'flowSkippedGroup',
        attrs: { 'data-seat-status': kind, 'data-seats': seats.join(',') },
        text: `${seats.map((s) => seatLabel(s, hand)).join(' · ')} ${label}`,
      })
    })
    .filter((k): k is HTMLElement => k != null)
  if (!kids.length) return null
  return el('p', { className: 'muted flowSkippedSeats', attrs: { 'data-flow-seat-status': '' } }, kids)
}


/**
 * Soft Quiet C2 Soft-safe ⑤: 「기억 안 남」 — empty until pressed
 * (never chipActive / aria-pressed true by default). Quiet on Line only;
 * smaller / later than share L1. Does not appear on Share / Copy / PNG.
 */
function renderMemoryCutControl(): HTMLElement {
  const ui = t()
  return el('div', { className: 'flowMemoryCutRow' }, [
    el('button', {
      className: 'chip flowActionChip flowMemoryCutChip',
      attrs: {
        type: 'button',
        'data-flow-memory-cut': '',
        'aria-pressed': 'false',
      },
      text: ui.flowMemoryCut,
      on: {
        click: () => {
          sizeOpenFor = null
          customSizeOpen = false
          cutFlowMemory()
        },
      },
    }),
  ])
}

function endingChip(
  text: string,
  pressed: boolean,
  onClick: () => void,
  data: Record<string, string>,
): HTMLElement {
  // Equal-weight neutral chips (existing .chip / .chipActive) — never
  // pre-pressed; pressed only after the user taps.
  return el('button', {
    className: ['chip', 'endingChip', pressed ? 'chipActive' : ''].filter(Boolean).join(' '),
    attrs: { type: 'button', 'aria-pressed': pressed ? 'true' : 'false', ...data },
    text,
    on: { click: onClick },
  })
}

/**
 * Soft G (A): optional ending record — one Quiet <details> row after the line,
 * closed by default with empty values. Records only what the user presses:
 * cards shown at showdown per seat (cards / 「안 보여줌(머크)」 / 「모름」) and
 * where the pot went (「<seat> 팟 가져감」 / 「나눔」 / 「모름」).
 */
function renderEndingRecord(hand: HandState): HTMLElement | null {
  // A-reset: a new hand always starts with the ending row closed.
  syncEndingHand()
  if (hand.actions.length === 0) return null
  const seats = endingSeats(hand)
  if (!seats.length) return null
  const ui = t()
  const shown = hand.ending?.shown ?? {}
  const pot = hand.ending?.pot
  if (endingPickSeat && !seats.includes(endingPickSeat)) endingPickSeat = null

  const showRows = seats.map((seat) => {
    const code = formatSeatCode(seat, hand.playerCount)
    const mark = shown[seat]
    const cards = Array.isArray(mark) ? mark : null
    const cardsText = cards
      ? cards.filter((c): c is Card => Boolean(c)).map((c) => `${c[0]}${SUIT_SYMBOL[c[1] as keyof typeof SUIT_SYMBOL]}`).join('')
      : ''
    const picking = endingPickSeat === seat
    const chips = [
      endingChip(cardsText || ui.endingCardsInput, Boolean(cards) || picking, () => {
        if (cards) {
          endingPickSeat = null
          clearEndingShown(seat)
          return
        }
        endingPickSeat = picking ? null : seat
        rerender()
      }, { 'data-ending-cards': seat }),
      endingChip(ui.endingMuck, mark === 'muck', () => {
        endingPickSeat = null
        setEndingShownMark(seat, 'muck')
      }, { 'data-ending-muck': seat }),
      endingChip(ui.endingUnknown, mark === 'unknown', () => {
        endingPickSeat = null
        setEndingShownMark(seat, 'unknown')
      }, { 'data-ending-unknown': seat }),
    ]
    let grid: HTMLElement | null = null
    if (picking && !(cards && cards[0] && cards[1])) {
      // Cards already on the table / board / other seats are taken; this seat's
      // own recorded holes stay pickable (the user re-enters what was shown).
      const used = usedCards(hand)
      const own: (Card | null)[] =
        seat === hand.heroPosition ? hand.holeCards : (hand.knownHoles?.[seat] ?? [])
      for (const c of own) if (c) used.delete(c)
      for (const [s, m] of Object.entries(shown)) {
        if (Array.isArray(m)) for (const c of m) if (c && s !== seat) used.add(c)
      }
      for (const c of cards ?? []) if (c) used.add(c)
      grid = cardGrid({
        used,
        onPick: (c: Card) => {
          if (cards && (cards[0] || cards[1])) endingPickSeat = null
          addEndingShownCard(seat, c)
        },
      })
    }
    return el('div', { className: 'field endingSeatRow', attrs: { 'data-ending-seat': seat } }, [
      el('span', { className: 'label', text: code }),
      el('div', {
        className: 'actorChips endingChips',
        attrs: { role: 'group', 'aria-label': ui.endingShownAria(code) },
      }, chips),
      grid,
    ])
  })

  const potSeatChips = [
    ...seats.map((seat) =>
      endingChip(
        ui.endingPotTo(formatSeatCode(seat, hand.playerCount)),
        pot?.kind === 'seat' && pot.seat === seat,
        () => setEndingPot({ kind: 'seat', seat }),
        { 'data-ending-to': seat },
      ),
    ),
  ]
  // Split / unknown on their own row so the seat chips never overflow at 390px.
  const potOtherChips = [
    endingChip(ui.endingSplit, pot?.kind === 'split', () => setEndingPot({ kind: 'split' }), { 'data-ending-to': '=' }),
    endingChip(ui.endingUnknown, pot?.kind === 'unknown', () => setEndingPot({ kind: 'unknown' }), { 'data-ending-to': '?' }),
  ]

  const details = el(
    'details',
    { className: 'endingChrome', attrs: endingOpen ? { open: '' } : undefined },
    [
      el('summary', { className: 'endingSummary', text: ui.endingSummary }),
      el('div', { className: 'endingBody' }, [
        el('span', { className: 'label', text: ui.endingShownLabel }),
        ...showRows,
        el('div', { className: 'field endingPotRow' }, [
          el('span', { className: 'label', text: ui.endingPotLabel }),
          el('div', {
            className: 'endingPotChips',
            attrs: { role: 'group', 'aria-label': ui.endingPotLabel },
          }, [
            el('div', { className: 'actorChips endingChips' }, potSeatChips),
            el('div', { className: 'actorChips endingChips' }, potOtherChips),
          ]),
        ]),
      ]),
    ],
  )
  details.addEventListener('toggle', () => {
    endingOpen = (details as HTMLDetailsElement).open
  })
  return details
}

export function renderActions(): HTMLElement {
  const ui = t()
  const { hand } = getState()
  const flow = deriveHandFlow(hand)
  const body =
    flow.phase.kind === 'board'
      ? renderBoardAsk(flow.phase.street, hand)
      : flow.phase.kind === 'actions'
        ? renderActionPad(hand)
        : el('p', { className: 'muted flowDone', attrs: { role: 'status' }, text: ui.flowDone })
  // Soft Quiet C2: mid-cut only while still asking (actions / board) — not on Share.
  const memoryCut =
    flow.phase.kind === 'actions' || flow.phase.kind === 'board'
      ? renderMemoryCutControl()
      : null
  return el('section', { className: 'panel panelLine panelFlow' }, [
    el('h2', { className: 'panelTitle', attrs: { tabindex: -1 }, text: ui.actionsTitle }),
    renderSkippedSeats(hand, flow.seatStatus),
    body,
    memoryCut,
    // Soft G: optional ending after the line — Quiet, closed, never asked for.
    renderEndingRecord(hand),
  ])
}
